import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Search, ShieldCheck, X, Loader2, AlertCircle, CheckCircle2, PauseCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  ACTIVE:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  BLOCKED: "bg-red-50 text-red-700 border-red-200",
  REVIEW:  "bg-amber-50 text-amber-800 border-amber-200",
};
const STATUS_ICON = { ACTIVE: CheckCircle2, BLOCKED: AlertCircle, REVIEW: PauseCircle };
const STATUSES = ["ACTIVE","BLOCKED","REVIEW"];

export default function AdminCompliance() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ACTIVE:0,BLOCKED:0,REVIEW:0});
  const [stateCounts, setStateCounts] = useState({ACTIVE:0,BLOCKED:0,REVIEW:0});
  const [addlCounts, setAddlCounts] = useState({ACTIVE:0,BLOCKED:0,REVIEW:0});
  const [stateTotal, setStateTotal] = useState(0);
  const [addlTotal, setAddlTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/compliance/states");
    setRows(data.states); setCounts(data.counts);
    setStateCounts(data.state_counts || data.counts);
    setAddlCounts(data.additional_jurisdictions_counts || {ACTIVE:0,BLOCKED:0,REVIEW:0});
    setStateTotal(data.state_total || 0);
    setAddlTotal(data.additional_jurisdictions_total || 0);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (q && !r.state.toLowerCase().includes(q.toLowerCase())
          && !r.state_code.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, statusFilter]);

  const open = async (row) => {
    const { data } = await api.get(`/admin/compliance/states/${row.id}`);
    setSel(data); setForm({});
    setAudit(data.audit || []);
  };

  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/compliance/states/${sel.id}`, form);
      toast.success(`${sel.state} updated`);
      setSel({...data, audit});
      setForm({});
      await load();
      const audRes = await api.get(`/admin/compliance/states/${sel.id}`);
      setAudit(audRes.data.audit || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Compliance · Direct Hire V1</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">State Availability</h1>
          <p className="text-sm text-slate-500 mt-1">Strict allowlist. A state is unavailable unless explicitly ACTIVE. Only authorized admins can change status — every change is audited.</p>
        </div>
        <div className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-4 py-2 font-semibold" data-testid="banner-active-states">
          ConnectMyHub Direct Hire V1: <span className="text-emerald-700">{stateCounts.ACTIVE} Active</span> · <span className="text-red-600">{stateCounts.BLOCKED} Blocked</span> · <span className="text-amber-700">{stateCounts.REVIEW} Review</span> · <span className="text-slate-500 font-normal">{stateTotal} U.S. states</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <StatCard label="Active" value={stateCounts.ACTIVE} tone="emerald" testId="stat-active"/>
        <StatCard label="Blocked" value={stateCounts.BLOCKED} tone="red" testId="stat-blocked"/>
        <StatCard label="Review" value={stateCounts.REVIEW} tone="amber" testId="stat-review"/>
      </div>

      {addlTotal > 0 && (
        <div data-testid="additional-jurisdictions" className="mb-5 bg-white border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-600 flex flex-wrap items-center gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Additional Jurisdictions</span>
          {rows.filter(r => (r.jurisdiction_type || "STATE") !== "STATE").map(r => (
            <span key={r.state_code} className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[r.status]}`}>
              {r.state} ({r.state_code}) · {r.status}
            </span>
          ))}
          <span className="text-slate-400 ml-auto">Federal district/other · not counted in the 50-state total</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="compliance-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search state or code…" className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-sky-500"/>
          </div>
          <select data-testid="compliance-status-filter" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Effective</th>
                <th className="text-right px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const Icon = STATUS_ICON[r.status];
                return (
                  <tr key={r.id} data-testid={`state-row-${r.state_code}`} onClick={()=>open(r)}
                      className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-4 py-2.5 font-semibold text-slate-900">{r.state}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.state_code}</td>
                    <td className="px-4 py-2.5 text-[10px]">
                      {(r.jurisdiction_type || "STATE") === "STATE"
                        ? <span className="text-slate-500">State</span>
                        : <span className="text-sky-700 font-semibold">Federal District</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[r.status]}`}>
                        <Icon size={11}/> {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs max-w-md truncate">{r.reason || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{r.effective_date ? r.effective_date.slice(0,10) : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-[10px] text-slate-400">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-500">No states match your filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={()=>setSel(null)}>
          <div onClick={e=>e.stopPropagation()} data-testid="compliance-drawer" className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600">Compliance record</div>
                <h3 className="font-display font-bold text-xl text-slate-900">{sel.state} <span className="font-mono text-slate-400 text-sm">({sel.state_code})</span></h3>
                <p className="text-xs text-slate-500">{sel.service_type} · current status <span className={`ml-1 font-semibold ${sel.status==='ACTIVE'?'text-emerald-700':sel.status==='BLOCKED'?'text-red-700':'text-amber-700'}`}>{sel.status}</span></p>
              </div>
              <button onClick={()=>setSel(null)} className="w-8 h-8 rounded hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Status</label>
                <div className="flex gap-2">
                  {STATUSES.map(s => (
                    <button key={s} data-testid={`set-status-${s}`}
                            onClick={()=>setForm({...form, status: s})}
                            className={`px-3 h-8 rounded border text-xs font-semibold ${(form.status||sel.status)===s ? STATUS_TONE[s] : "bg-white border-slate-300 text-slate-600"}`}>{s}</button>
                  ))}
                </div>
                {(form.status||sel.status) === "ACTIVE" && !sel.effective_date && !form.effective_date && (
                  <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Activation requires an effective date below.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Fld label="Effective date" k="effective_date" type="date" form={form} setForm={setForm} defaultVal={sel.effective_date?.slice(0,10)}/>
                <Fld label="Expiration date" k="expiration_date" type="date" form={form} setForm={setForm} defaultVal={sel.expiration_date?.slice(0,10)}/>
                <Fld label="Next review date" k="next_review_date" type="date" form={form} setForm={setForm} defaultVal={sel.next_review_date?.slice(0,10)}/>
                <Fld label="License / registration #" k="license_or_registration_number" form={form} setForm={setForm} defaultVal={sel.license_or_registration_number}/>
                <Fld label="State fee ($)" k="state_fee" type="number" form={form} setForm={setForm} defaultVal={sel.state_fee}/>
                <Fld label="Renewal fee ($)" k="renewal_fee" type="number" form={form} setForm={setForm} defaultVal={sel.renewal_fee}/>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Toggle label="License required" k="license_required" form={form} setForm={setForm} defaultVal={sel.license_required}/>
                <Toggle label="Registration required" k="registration_required" form={form} setForm={setForm} defaultVal={sel.registration_required}/>
                <Toggle label="Bond required" k="bond_required" form={form} setForm={setForm} defaultVal={sel.bond_required}/>
              </div>

              <TextArea label="Reason" k="reason" form={form} setForm={setForm} defaultVal={sel.reason}/>
              <TextArea label="Occupation restrictions" k="occupation_restrictions" form={form} setForm={setForm} defaultVal={sel.occupation_restrictions}/>
              <TextArea label="Special conditions" k="special_conditions" form={form} setForm={setForm} defaultVal={sel.special_conditions}/>
              <TextArea label="Source reference (statute, memo, license page URL)" k="source_reference" form={form} setForm={setForm} defaultVal={sel.source_reference}/>
              <TextArea label="Admin notes" k="admin_notes" form={form} setForm={setForm} defaultVal={sel.admin_notes}/>

              <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                <button data-testid="save-compliance-btn" disabled={busy || Object.keys(form).length===0} onClick={save}
                        className="px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                  {busy && <Loader2 size={14} className="animate-spin"/>} Save changes
                </button>
                <div className="text-[11px] text-slate-500">Every change creates an immutable audit event.</div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Audit trail ({audit.length})</div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {audit.length === 0 && <div className="text-xs text-slate-400">No audit events yet.</div>}
                  {audit.map(a => (
                    <div key={a.id} className="p-2.5 rounded border border-slate-200 bg-slate-50 text-xs">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-semibold text-slate-800">
                          {a.status_change ? `${a.previous_status} → ${a.new_status}` : "Field update"}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-slate-600">By <span className="font-semibold">{a.user_email || a.user_id}</span> ({a.user_role})</div>
                      {a.after?.reason && <div className="text-slate-500 italic mt-0.5">"{a.after.reason}"</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const StatCard = ({label, value, tone, testId}) => (
  <div data-testid={testId} className={`bg-white border rounded-lg p-4 shadow-sm border-${tone}-200`}>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label} states</div>
    <div className={`font-display text-3xl font-bold text-${tone}-800 tracking-tight`}>{value}</div>
  </div>
);
const Fld = ({label, k, type="text", form, setForm, defaultVal}) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    <input type={type} data-testid={`fld-${k}`} defaultValue={defaultVal || ""}
           onBlur={e=>setForm({...form, [k]: type==="number" ? parseFloat(e.target.value)||null : e.target.value || null})}
           className="w-full h-9 px-3 rounded border border-slate-300 text-sm"/>
  </div>
);
const TextArea = ({label, k, form, setForm, defaultVal}) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    <textarea rows={2} data-testid={`fld-${k}`} defaultValue={defaultVal || ""}
              onBlur={e=>setForm({...form, [k]: e.target.value || null})}
              className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
  </div>
);
const Toggle = ({label, k, form, setForm, defaultVal}) => {
  const cur = form[k] === undefined ? defaultVal : form[k];
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <div className="flex gap-1">
        <button data-testid={`toggle-${k}-yes`} onClick={()=>setForm({...form,[k]:true})}
                className={`flex-1 h-8 rounded border text-xs font-semibold ${cur===true?"bg-emerald-50 border-emerald-300 text-emerald-800":"bg-white border-slate-300 text-slate-600"}`}>Yes</button>
        <button data-testid={`toggle-${k}-no`} onClick={()=>setForm({...form,[k]:false})}
                className={`flex-1 h-8 rounded border text-xs font-semibold ${cur===false?"bg-slate-100 border-slate-400 text-slate-800":"bg-white border-slate-300 text-slate-600"}`}>No</button>
      </div>
    </div>
  );
};
